import { useEffect, useMemo, useState } from "react";
import { bulkEditSipClients, resolveSipClients } from "../../../admin/api";
import { useWorkspaceSelection } from "../workspaceSelection/WorkspaceSelectionContext";
import { createSipClientSelectionItemFromTransfer, createTransferSelectionItem, getWorkspaceSelectionKey, normalizeTransferNumber, prepareUniqueSipItems } from "../workspaceSelection/workspaceSelectionUtils";
import { buildSipEditorPatch, createSipEditorDraft } from "./bulkSipTableEditorUtils";
import { useSessionSipActivity } from "../sessionActivity/SessionSipActivityContext";
import { useAdminNotifications } from "../notifications/AdminNotificationsContext";
import { useAdminDataRefresh } from "../dataRefresh/AdminDataRefreshContext";
import AdminModalPortal from "../shared/AdminModalPortal";
import ActionIconButton from "../shared/ActionIconButton";
import "../shared/BulkTableEditor.css";

const styles = {
  title: { margin: 0, color: "var(--text-main)", fontSize: "20px" },
  summary: { margin: "7px 0", color: "var(--text-muted)", fontSize: "13px" },
  scroll: { maxHeight: "62vh", overflow: "auto", border: "1px solid var(--border)", borderRadius: "10px" },
  table: { width: "100%", minWidth: 0, fontSize: "12px" },
  cell: { padding: "var(--admin-table-cell-y) var(--admin-table-cell-x)", borderBottom: "1px solid var(--border)", color: "var(--text-main)", verticalAlign: "middle", textAlign: "left" },
  input: { width: "100%", boxSizing: "border-box", padding: "6px", borderRadius: "6px", border: "1px solid var(--border-strong)", background: "var(--field-bg)", color: "var(--text-main)" },
  changed: { background: "var(--accent-soft)", borderColor: "var(--accent-border)" },
  rowError: { background: "var(--danger-soft)" },
  badge: { display: "inline-flex", padding: "3px 6px", borderRadius: "999px", background: "var(--success-soft)", color: "var(--success)", fontWeight: 800 },
  error: { color: "var(--danger)", fontSize: "11px", marginTop: "3px" },
  warning: { color: "var(--danger)", fontSize: "12px", marginTop: "5px" },
  actions: { display: "flex", justifyContent: "space-between", gap: "8px", marginTop: "14px", flexWrap: "wrap" },
  button: { border: "1px solid var(--border-strong)", borderRadius: "8px", padding: "8px 11px", background: "var(--card-bg-soft)", color: "var(--text-main)", fontWeight: 800, cursor: "pointer" },
  primary: { border: "none", background: "var(--accent)", color: "var(--button-text)" },
};

const RESOLVE_ERROR_LABELS = {
  transfer_not_found: "Перевод больше не найден или его номер изменился",
  sip_client_not_found: "Клиент больше не найден",
  complex_not_found: "ЖК больше не найден",
};

export default function BulkSipTableEditorModal({ isOpen, items, entityType = "sipClient", excludedComplexCount = 0, sourceSection = "admin", onClose, onApplied }) {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [backendErrors, setBackendErrors] = useState({});
  const [summary, setSummary] = useState(null);
  const { replaceItems, updateSnapshots } = useWorkspaceSelection();
  const { recordChanges } = useSessionSipActivity();
  const { notify } = useAdminNotifications();
  const { invalidate } = useAdminDataRefresh();
  const selectionItems = useMemo(() => {
    if (entityType !== "transfer") return prepareUniqueSipItems(items);
    const unique = new Map();
    items.forEach((item) => {
      if (item?.type !== "transfer") return;
      const key = getWorkspaceSelectionKey(item);
      if (key) unique.set(key, item);
    });
    return Array.from(unique.values());
  }, [entityType, items]);
  const selectionSignature = selectionItems.map(getWorkspaceSelectionKey).join("|");

  useEffect(() => {
    if (!isOpen) return undefined;
    let active = true;

    async function load() {
      try {
        setIsLoading(true); setError(""); setBackendErrors({}); setSummary(null);
        const response = await resolveSipClients({ items: selectionItems.map((item) => ({ complexId: item.complexId, sipClientId: item.sipClientId })) });
        if (!active) return;
        const resolvedByKey = new Map(response.results.map((row) => [row.key, row]));
        const nextRows = entityType === "transfer"
          ? selectionItems.map((item) => {
              const transferKey = getWorkspaceSelectionKey(item);
              const sipItem = createSipClientSelectionItemFromTransfer(item);
              const resolved = resolvedByKey.get(getWorkspaceSelectionKey(sipItem));
              if (!resolved?.ok) return { ...(resolved || {}), key: transferKey, complexId: item.complexId, sipClientId: item.sipClientId, clientName: item.clientName, ok: false, error: resolved?.error || "transfer_not_found" };
              if (normalizeTransferNumber(resolved.transfer_number) !== normalizeTransferNumber(item.transfer_number)) {
                return { ...resolved, key: transferKey, backendKey: resolved.key, ok: false, error: "transfer_not_found" };
              }
              return { ...resolved, key: transferKey, backendKey: resolved.key, selectionItem: item };
            })
          : response.results;
        setRows(nextRows);
        setDrafts(Object.fromEntries(nextRows.filter((row) => row.ok).map((row) => [row.key, createSipEditorDraft(row)])));
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : "Не удалось загрузить клиентов");
      } finally { if (active) setIsLoading(false); }
    }
    load();
    return () => { active = false; };
  // Snapshots may update after a partial save; resolve again only when selected keys change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectionSignature]);

  const changes = useMemo(() => {
    const byKey = {};
    let changedRows = 0;
    let changedFields = 0;
    let validationErrors = 0;
    rows.filter((row) => row.ok).forEach((row) => {
      const value = buildSipEditorPatch(row, drafts[row.key] || createSipEditorDraft(row));
      byKey[row.key] = value;
      if (value.changedFieldCount) changedRows += 1;
      changedFields += value.changedFieldCount;
      validationErrors += Object.keys(value.errors).length;
    });
    return { byKey, changedRows, changedFields, validationErrors };
  }, [drafts, rows]);
  const uniqueComplexCount = new Set(rows.filter((row) => row.ok).map((row) => row.complexId)).size;

  const changeDraft = (key, field, value) => {
    setDrafts((current) => ({ ...current, [key]: { ...current[key], [field]: value } }));
    setBackendErrors((current) => ({ ...current, [key]: "" }));
    setSummary(null);
  };
  const resetRow = (row) => {
    setDrafts((current) => ({ ...current, [row.key]: createSipEditorDraft(row) }));
    setBackendErrors((current) => ({ ...current, [row.key]: "" }));
  };
  const resetAll = () => {
    setDrafts(Object.fromEntries(rows.filter((row) => row.ok).map((row) => [row.key, createSipEditorDraft(row)])));
    setBackendErrors({});
    setSummary(null);
  };

  const save = async () => {
    const changed = rows.filter((row) => row.ok && changes.byKey[row.key]?.changedFieldCount);
    if (!changed.length || changes.validationErrors) return;
    try {
      setIsSaving(true); setError(""); setBackendErrors({});
      const response = await bulkEditSipClients({ items: changed.map((row) => ({ complexId: row.complexId, sipClientId: row.sipClientId, patch: changes.byKey[row.key].patch })) }, sourceSection);
      const resultsByKey = new Map(response.results.map((item) => [item.key, item]));
      const nextRows = rows.map((row) => {
        const result = resultsByKey.get(row.backendKey || row.key);
        if (!result?.ok) return row;
        const updatedRow = { ...row, ...result.client };
        if (entityType !== "transfer") return updatedRow;
        const selectionItem = createTransferSelectionItem({
          complexId: result.complexId,
          sipClientId: result.sipClientId,
          complexName: row.complexName,
          clientName: result.clientName,
          transfer_number: result.client.transfer_number,
          clientStatus: result.client.status,
        });
        return { ...updatedRow, key: getWorkspaceSelectionKey(selectionItem), selectionItem };
      });
      setRows(nextRows);
      setDrafts((current) => {
        const next = { ...current };
        nextRows.forEach((row) => { if (resultsByKey.get(row.backendKey || row.key)?.ok) next[row.key] = createSipEditorDraft(row); });
        return next;
      });
      setBackendErrors(Object.fromEntries(changed.map((row) => {
        const result = resultsByKey.get(row.backendKey || row.key);
        return result && !result.ok ? [row.key, result.error] : null;
      }).filter(Boolean)));
      setSummary(response);
      const successfulRows = changed.map((row) => ({ row, result: resultsByKey.get(row.backendKey || row.key) })).filter(({ result }) => result?.ok);
      replaceItems(successfulRows.map(({ row, result }) => ({
        key: entityType === "transfer"
          ? row.key
          : getWorkspaceSelectionKey(createTransferSelectionItem({ complexId: row.complexId, sipClientId: row.sipClientId, clientName: row.clientName, transfer_number: row.transfer_number })),
        item: createTransferSelectionItem({
          complexId: result.complexId,
          sipClientId: result.sipClientId,
          complexName: row.complexName,
          clientName: result.clientName,
          transfer_number: result.client.transfer_number,
          clientStatus: result.client.status,
        }),
      })).filter((update) => update.key));
      updateSnapshots(successfulRows.map(({ result }) => ({ key: result.key, snapshot: { sipNumber: result.client.sip_number, transferNumber: result.client.transfer_number, type: result.client.type, offerPrice: result.client.offerPrice, clientStatus: result.client.status } })));
      if (successfulRows.length) invalidate(["complexes", "sip", "transfers"]);
      recordChanges(
        successfulRows.filter(({ result }) => result.changed).map(({ row, result }) => {
          const selected = entityType === "transfer" ? createSipClientSelectionItemFromTransfer(row.selectionItem) : selectionItems.find((item) => getWorkspaceSelectionKey(item) === result.key);
          return {
            ...selected,
            changedFields: Object.keys(result.patch || {}),
            snapshot: {
              ...selected?.snapshot,
              sipNumber: result.client.sip_number,
              transferNumber: result.client.transfer_number,
              type: result.client.type,
              clientStatus: result.client.status,
            },
          };
        }),
        "bulk-table-edit"
      );
      onApplied?.(response.results);
      const entityLabel = entityType === "transfer" ? "СИПы" : "Клиенты";
      notify(`${entityLabel}: ${response.changedCount} изменено, ${response.unchangedCount} без изменений, ${response.failedCount} ошибок`, response.failedCount ? "partial" : "success");
    } catch (saveError) { const message = saveError instanceof Error ? saveError.message : "Не удалось сохранить изменения"; setError(message); notify(message, "error"); }
    finally { setIsSaving(false); }
  };

  if (!isOpen) return null;
  return (
    <AdminModalPortal
      isOpen={isOpen}
      onClose={onClose}
      closeDisabled={isSaving}
      ariaLabel={entityType === "transfer" ? "Табличный редактор СИПов" : "Табличный редактор клиентов"}
    >
        <h2 style={styles.title}>{entityType === "transfer" ? "Редактировать таблицу СИПов" : "Редактировать таблицу клиентов"}</h2>
        <p style={styles.summary}>{entityType === "transfer" ? "СИПы" : "Клиенты"}: {selectionItems.length} · ЖК: {uniqueComplexCount} · элементов ЖК не участвует: {excludedComplexCount} · изменено строк: {changes.changedRows} · полей: {changes.changedFields}</p>
        {error && <div style={styles.warning}>{error}</div>}
        {summary && <div style={summary.failedCount ? styles.warning : styles.summary}>Изменено: {summary.changedCount}. Без изменений: {summary.unchangedCount}. Ошибок: {summary.failedCount}.</div>}
        {isLoading ? <div style={styles.summary}>Загрузка актуальных данных...</div> : (
          <div className="bulk-table-editor__scroll"><table className="bulk-table-editor__table bulk-table-editor__table--client" style={styles.table}>
            <thead><tr>
              {(entityType === "transfer" ? ["ЖК", "Номер перевода", "СИП", "Клиент", "Статус"] : ["ЖК", "Тип перевода", "СИП", "Номер перевода", "Цена оффера", "Статус", "Клиент"]).map((label) => <th key={label} className={`bulk-table-editor__cell${label === "ЖК" ? " bulk-table-editor__cell--name" : label === "Статус" ? " bulk-table-editor__cell--status" : ""}`}>{label}</th>)}
              <th className="bulk-table-editor__cell bulk-table-editor__cell--actions">Действия</th>
            </tr></thead>
            <tbody>{rows.map((row) => {
              if (!row.ok) return <tr key={row.key} style={styles.rowError}><td colSpan={entityType === "transfer" ? 6 : 8} className="bulk-table-editor__cell">{row.clientName}: {RESOLVE_ERROR_LABELS[row.error] || row.error}</td></tr>;
              const draft = drafts[row.key] || createSipEditorDraft(row);
              const info = changes.byKey[row.key] || { patch: {}, errors: {}, changedFieldCount: 0 };
              const backendError = backendErrors[row.key];
              const hasRowChanges = Boolean(info.changedFieldCount || Object.keys(info.errors).length);
              const fieldCell = (field) => <td key={field} className="bulk-table-editor__cell"><input type="text" className={`bulk-table-editor__input${Object.prototype.hasOwnProperty.call(info.patch, field) ? " bulk-table-editor__input--changed" : ""}`} value={draft[field]} onChange={(event) => changeDraft(row.key, field, event.target.value)} />{info.errors[field] && <div style={styles.error}>{info.errors[field]}</div>}</td>;
              const statusCell = <td className="bulk-table-editor__cell bulk-table-editor__cell--status"><select className={`bulk-table-editor__input ${draft.status ? "bulk-table-editor__status--client-active" : "bulk-table-editor__status--client-stopped"}${Object.prototype.hasOwnProperty.call(info.patch, "status") ? " bulk-table-editor__input--changed" : ""}`} value={draft.status ? "active" : "stopped"} onChange={(event) => changeDraft(row.key, "status", event.target.value === "active")}><option value="active">Активен</option><option value="stopped">В стопе</option></select></td>;
              const clientCell = <td className="bulk-table-editor__cell">{row.clientName || "—"}{backendError && <div style={styles.error}>{backendError}</div>}</td>;
              return <tr key={row.key} style={backendError ? styles.rowError : undefined}>
                <td className="bulk-table-editor__cell bulk-table-editor__cell--name"><strong>{row.complexName}</strong>{row.isCurrentClient && <><br /><span style={styles.badge}>На карте</span></>}</td>
                {entityType === "transfer" ? <>{fieldCell("transfer_number")}{fieldCell("sip_number")}{clientCell}{statusCell}</> : <>{fieldCell("type")}{fieldCell("sip_number")}{fieldCell("transfer_number")}{fieldCell("offerPrice")}{statusCell}{clientCell}</>}
                <td className="bulk-table-editor__cell bulk-table-editor__cell--actions"><ActionIconButton icon="reset" label="Сбросить изменения строки" disabled={isSaving || !hasRowChanges} onClick={() => resetRow(row)} /></td>
              </tr>;
            })}</tbody>
          </table></div>
        )}
        <div style={styles.actions}><button type="button" style={styles.button} disabled={isSaving} onClick={resetAll}>Сбросить все изменения</button><div><button type="button" style={styles.button} disabled={isSaving} onClick={onClose}>Отмена</button>{" "}<button type="button" style={{ ...styles.button, ...styles.primary }} disabled={isSaving || isLoading || !changes.changedRows || Boolean(changes.validationErrors)} onClick={save}>{isSaving ? "Сохраняется..." : "Сохранить изменения"}</button></div></div>
    </AdminModalPortal>
  );
}
